import { createTheme } from '@mui/material/styles';
import { T } from './tokens';

export const synterraTheme = createTheme({
    palette: {
        primary:    { main: T.color.teal[800], dark: T.color.teal[900], light: T.color.teal[600] },
        secondary:  { main: T.color.magenta[500], dark: T.color.magenta[600], light: T.color.magenta[400] },
        success:    { main: T.color.status.green },
        warning:    { main: T.color.status.amber },
        error:      { main: T.color.status.red },
        info:       { main: T.color.status.blue },
        background: { default: T.color.surface.canvas, paper: T.color.surface.white },
        text:       { primary: T.color.text.primary, secondary: T.color.text.secondary },
    },
    typography: {
        fontFamily: T.font.family,
        h1: { fontWeight: 800, letterSpacing: '-0.030em', lineHeight: 1.10 },
        h2: { fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15 },
        h3: { fontWeight: 700, letterSpacing: '-0.020em', lineHeight: 1.20 },
        h4: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.25 },
        h5: { fontWeight: 600, letterSpacing: '-0.010em', lineHeight: 1.30 },
        h6: { fontWeight: 600, letterSpacing: '0.000em',  lineHeight: 1.35 },
        body1:   { fontSize: '0.9375rem', lineHeight: 1.60 },
        body2:   { fontSize: '0.875rem',  lineHeight: 1.55 },
        caption: { fontSize: '0.750rem',  fontWeight: 500, letterSpacing: '0.04em' },
        overline: { fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' as const },
        button:  { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' as const },
    },
    shape: { borderRadius: T.radius.lg },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: T.font.family,
                    backgroundColor: T.color.surface.canvas,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: T.shadow.card,
                    border: `1px solid ${T.color.border.light}`,
                    borderRadius: T.radius.lg,
                    backgroundImage: 'none',
                    transition: 'box-shadow 0.18s ease, transform 0.18s ease',
                    '&:hover': {
                        boxShadow: T.shadow.md,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none' },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: T.radius.sm,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.01em',
                    lineHeight: 1.4,
                },
                contained: {
                    boxShadow: T.shadow.button,
                    '&:hover': { boxShadow: T.shadow.buttonHover },
                },
                outlined: {
                    borderColor: T.color.border.medium,
                    '&:hover': {
                        borderColor: T.color.teal[500],
                        backgroundColor: 'rgba(10,42,56,0.04)',
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    letterSpacing: '0.02em',
                    borderRadius: T.radius.sm,
                },
            },
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined' as const, size: 'small' as const },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: T.radius.md,
                        fontSize: '0.9rem',
                        fontFamily: T.font.family,
                        '& fieldset': { borderColor: T.color.border.medium },
                        '&:hover fieldset': { borderColor: T.color.teal[500] },
                        '&.Mui-focused fieldset': { borderColor: T.color.teal[600], borderWidth: 2 },
                    },
                    '& .MuiInputLabel-root': {
                        fontFamily: T.font.family,
                        fontSize: '0.875rem',
                        '&.Mui-focused': { color: T.color.teal[600] },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: { borderRadius: T.radius.md },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: T.color.border.light },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: { borderRadius: T.radius.full, height: 6 },
                bar:  { borderRadius: T.radius.full },
            },
        },
    },
});
